// Single source of truth for "render a price to the user".
//
// Today: every price the backend hands us is in KLV (whole units, 6-decimal
// precision). When the Phase 5 oracle lands and we add USD / BTC displays
// (or a user preference toggle), the swap is internal to this file —
// every call site already routes through here, so no display code has to
// change again. Same pattern as `formatName` (#176) and `formatHandle`.

export type DisplayUnit = 'klv' | 'usd' | 'btc';

/** Default unit when caller doesn't pass one. Lives here so future user
 *  preferences (Redux/context) can override the default in one place. */
export const DEFAULT_DISPLAY_UNIT: DisplayUnit = 'klv';

const SYMBOL: Record<DisplayUnit, string> = {
  klv: 'KLV',
  usd: 'USD',
  btc: 'BTC',
};

const FRACTION_DIGITS: Record<DisplayUnit, { min: number; max: number }> = {
  // KLV is 6-decimal precision on-chain. Show 2 by default (cents-style); the
  // raw value is exact for math, just formatted compactly for users.
  klv: { min: 2, max: 2 },
  usd: { min: 2, max: 2 },
  // BTC commonly shows up to 8 decimals (satoshi precision).
  btc: { min: 4, max: 8 },
};

interface FormatPriceOpts {
  /** Display unit. Defaults to KLV until the oracle ships. */
  unit?: DisplayUnit;
  /** Override fraction-digit settings (uncommon). */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Drop the unit suffix and return just the number, formatted. */
  noSymbol?: boolean;
  /**
   * Disable the compact-large / extended-precision-small adaptive sizing
   * for callers that want raw `toFixed(min/max)` behavior. Defaults to
   * `false` (sizing is on).
   */
  exact?: boolean;
}

/**
 * Choose how many fraction digits to render based on the value's
 * magnitude. Tightens the display for the common cases the user reads
 * at a glance:
 *   • ≥ 1: standard cents-style (2 decimals).
 *   • 0 < x < 1: extend up to 4 decimals so values like 0.0002 KLV
 *     show as 0.0002 instead of rounding to 0.00.
 *   • 0: render as 0 with no trailing decimal noise.
 * Returns Intl.NumberFormat-compatible min/max digits.
 */
const adaptiveFractionDigits = (abs: number): { min: number; max: number } => {
  if (abs === 0) return { min: 0, max: 0 };
  if (abs < 1) return { min: 2, max: 4 };
  return { min: 2, max: 2 };
};

/**
 * Compact-form thresholds for large numbers. Render integers ≥ 1000
 * with a single-letter suffix so price displays don't stretch a card
 * into the next column.
 *
 *   100,000             → "100.00k"
 *   1,500,000           → "1.50m"
 *   2,300,000,000       → "2.30b"
 *   6,945,036,440,091   → "6.95t"
 *
 * Without the 't' tier, values in the trillions roll past the 'b' tier
 * into four-digit prefixes (e.g. "6,945.04b"), which defeats the point
 * of compacting. Order matters — most-significant suffix first so the
 * loop hits it before stepping down.
 */
const COMPACT_THRESHOLDS: Array<{ limit: number; suffix: string }> = [
  { limit: 1_000_000_000_000, suffix: 't' },
  { limit: 1_000_000_000, suffix: 'b' },
  { limit: 1_000_000, suffix: 'm' },
  { limit: 1_000, suffix: 'k' },
];

const formatCompact = (n: number): string => {
  for (const { limit, suffix } of COMPACT_THRESHOLDS) {
    if (Math.abs(n) >= limit) {
      return `${(n / limit).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}${suffix}`;
    }
  }
  // Below 1k — caller handles fraction digits.
  return '';
};

/**
 * Render a price for UI display. `amount` is in **whole units** of KLV
 * (the canonical unit the backend now serves for `priceKlv` / `price`
 * fields after Phase 2). Accepts number or numeric string; non-numeric
 * input returns an empty string.
 *
 * Adaptive sizing (default behavior, opt out with `exact: true`):
 *   • Values ≥ 1000 render in compact form (100k / 1.5m / 2.3b) so price
 *     labels stay narrow on cards.
 *   • Values < 1 render up to 4 decimal places so tiny minimums like
 *     0.0002 KLV don't round to 0.00.
 *   • Everything in between stays 2-decimal cents-style.
 *
 * Phase 5 oracle integration plan (when it lands):
 *   - Read user's preferred display unit from settings / context.
 *   - For non-KLV units, fetch latest KLV→USD or KLV→BTC rate from the
 *     oracle and convert before formatting.
 *   - Update `SYMBOL` / `FRACTION_DIGITS` if any rule needs adjustment.
 * Nothing else needs to change.
 */
export const formatPrice = (
  amount: number | string | null | undefined,
  opts: FormatPriceOpts = {},
): string => {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (!Number.isFinite(n)) return '';

  const unit = opts.unit ?? DEFAULT_DISPLAY_UNIT;

  // BTC keeps its unit-specific precision rules; adaptive sizing applies
  // to KLV/USD where humans expect ~2 decimals normally and more only
  // when the value is tiny.
  let formatted: string;
  if (opts.exact || unit === 'btc') {
    const { min, max } = FRACTION_DIGITS[unit];
    formatted = n.toLocaleString(undefined, {
      minimumFractionDigits: opts.minimumFractionDigits ?? min,
      maximumFractionDigits: opts.maximumFractionDigits ?? max,
    });
  } else {
    const abs = Math.abs(n);
    const compact = abs >= 1_000 ? formatCompact(n) : '';
    if (compact) {
      formatted = compact;
    } else {
      const { min, max } = adaptiveFractionDigits(abs);
      formatted = n.toLocaleString(undefined, {
        minimumFractionDigits: opts.minimumFractionDigits ?? min,
        maximumFractionDigits: opts.maximumFractionDigits ?? max,
      });
    }
  }

  if (opts.noSymbol) return formatted;
  return `${formatted} ${SYMBOL[unit]}`;
};
